import type { StyleRule } from '@archipro-design/aria';
import { buttonClassName } from '@archipro-design/aria';
import { boxClassName } from '@archipro-design/aria';
import { labelClassName } from '@archipro-design/aria';
import { inputSlotClassNames } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import { svgIconClassName } from '@archipro-design/icons';

const pressPopTransition = 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)';

export const ButtonBase: StyleRule = ({ theme }) => ({
    border: `${pxToRem(2)} solid ${theme.siteVariables.colors.charcoal[250]}`,
    backgroundColor: theme.siteVariables.colors.white['100A'],
    height: pxToRem(48),
    '&:not(:disabled):hover': {
        border: `${pxToRem(2)} solid ${
            theme.siteVariables.colors.charcoal[250]
        }`,
    },
    [theme.screen.min.tablet]: {
        height: pxToRem(66),
        '&:not(:disabled):hover': {
            backgroundColor: theme.siteVariables.colors.white['200A'],
        },
    },
    width: '100%',
    [`& .${svgIconClassName}`]: {
        transition: pressPopTransition,
    },
    '&:not(:disabled):active': {
        [`& .${svgIconClassName}`]: {
            transform: 'scale(1.3)',
        },
    },
});

export const QuantityGroup: StyleRule = () => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: pxToRem(-2),
});

export const PurchaseGroup: StyleRule = ({ theme }) => ({
    display: 'grid',
    width: '100%',
    gridTemplateColumns: `${pxToRem(48)} ${pxToRem(48)} ${pxToRem(
        48,
    )}  1fr ${pxToRem(48)}`,
    backgroundColor: theme.siteVariables.colors.white['100A'],
    marginTop: theme.siteVariables.spaces['space-24'],
    [theme.screen.tablet]: {
        marginTop: 0,
        gridTemplateColumns: `${pxToRem(65)} ${pxToRem(64)} ${pxToRem(65)} 1fr ${pxToRem(66)}`,
    },
});

export const SaveToDesignBoard: StyleRule = ({ theme }) => ({
    cursor: 'pointer',
    [theme.screen.tablet]: {
        '& > div': {
            height: 'auto',
        },
    },
});

export const ButtonSubstract: StyleRule<{ canAddToCart: boolean }> = ({
    theme,
    canAddToCart,
}) => ({
    border: `${pxToRem(2)} solid ${theme.siteVariables.colors.charcoal[250]}`,
    [`&.${buttonClassName} .${svgIconClassName}`]: {
        width: pxToRem(12),
        height: pxToRem(12),
        [theme.screen.tablet]: {
            width: pxToRem(16),
            height: pxToRem(16),
        },
    },
    opacity: canAddToCart ? 1 : 0.4,
    [theme.screen.tablet]: {
        '&:disabled svg': {
            fill: theme.siteVariables.colors.charcoal[250],
        },
    },
});

export const ButtonAdd: StyleRule<{ canAddToCart: boolean }> = ({
    theme,
    canAddToCart,
}) => ({
    border: `${pxToRem(2)} solid ${theme.siteVariables.colors.charcoal[250]}`,
    [`&.${buttonClassName} .${svgIconClassName}`]: {
        width: pxToRem(12),
        height: pxToRem(12),
        [theme.screen.tablet]: {
            width: pxToRem(17),
            height: pxToRem(17),
        },
    },
    opacity: canAddToCart ? 1 : 0.4,
    [`&.${buttonClassName}:disabled`]: {
        ['& .' + svgIconClassName]: {
            color: theme.siteVariables.colors.grey['200'],
            '& path': {
                fill: 'currentColor',
            },
            [theme.screen.tablet]: {
                color: theme.siteVariables.colors.charcoal[250],
            },
        },
    },
});

export const BoxInput: StyleRule<{ canAddToCart: boolean }> = ({
    theme,
    canAddToCart,
}) => ({
    width: '100%',
    height: pxToRem(48),
    opacity: canAddToCart ? 1 : 0.4,
    position: 'static',
    ':focus-within': {
        backgroundColor: theme.siteVariables.colors.white['200A'],
    },
    [theme.screen.tablet]: {
        height: pxToRem(66),
    },
    ['&.' + boxClassName]: {
        border: `${pxToRem(2)} solid ${
            theme.siteVariables.colors.charcoal[250]
        }`,
        borderLeft: 0,
        borderRight: 0,
    },
    ['& .' + inputSlotClassNames.input]: {
        borderBottom: 'none',
        textAlign: 'center',
        height: '100%',
        width: '100%',
        padding: 0,
        fontSize: `max(${pxToRem(16)}, 16px)`,
        [theme.screen.tablet]: {
            fontSize: pxToRem(16),
        },
        '&:disabled': {
            color: theme.siteVariables.colors.charcoal[250],
        },
    },
});

export const ButtonAddToCart: StyleRule<{ canAddToCart: boolean }> = ({
    theme,
    canAddToCart,
}) => ({
    border: `${pxToRem(2)} solid ${theme.siteVariables.colors.charcoal[250]}`,
    opacity: canAddToCart ? 1 : 0.4,
    backgroundColor: '#222222',
    textTransform: 'uppercase',
    height: pxToRem(48),
    ['&.' + buttonClassName]: {
        padding: 0,
        [`& .${svgIconClassName}`]: {
            width: pxToRem(32),
            height: pxToRem(32),
        },
    },
    ['& .' + labelClassName]: {
        letterSpacing: 'normal',
        lineHeight: theme.siteVariables.lineHeightLarge,
        transition: pressPopTransition,
        [theme.screen.tablet]: {
            fontSize: pxToRem(16),
            lineHeight: pxToRem(18),
        },
    },
    '&:not(:disabled):active': {
        ['& .' + labelClassName]: {
            transform: 'scale(1.1)',
        },
    },
    '&:disabled': {
        color: theme.siteVariables.colors.primitive.white,
        backgroundColor: theme.siteVariables.colors.charcoal[250],
    },

    [theme.screen.tablet]: {
        height: pxToRem(66),
        outlineWidth: pxToRem(2),
        [`&.${buttonClassName} .${svgIconClassName}`]: {
            width: pxToRem(22),
            height: pxToRem(22),
        },
    },
});

export const ButtonHeart: StyleRule = ({ theme }) => ({
    cursor: 'pointer',
    [`& .${buttonClassName}:hover`]: {
        background: 'transparent',
    },
    ['& .' + svgIconClassName]: {
        [theme.screen.tablet]: {
            width: pxToRem(22),
            height: pxToRem(22),
        },
    },
});

export const SampleRow: StyleRule = ({ theme }) => ({
    textAlign: 'right',
    fontSize: theme.siteVariables.fontSizes.caption03,
    lineHeight: pxToRem(24),
    color: theme.siteVariables.colors.charcoal[250],
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    '& > span': {
        cursor: 'pointer',
    },
    marginTop: pxToRem(23),
    [theme.screen.tablet]: {
        textDecoration: 'none',
        marginTop: pxToRem(21),
        width: pxToRem(426),
        fontSize: pxToRem(16),
        lineHeight: pxToRem(18),
        '&:hover': {
            textDecoration: 'underline',
            textDecorationSkipInk: 'none',
        },
    },
});

export const SaveShareContainer: StyleRule = ({ theme }) => ({
    paddingTop: pxToRem(25),
    paddingBottom: pxToRem(25),
    borderBottom: `${pxToRem(1)} solid ${theme.siteVariables.colors.grey[125]}`,
    justifyContent: 'flex-start',
});

export const LinkButton: StyleRule = () => ({
    [`&.${buttonClassName}`]: {
        textDecoration: 'underline',
    },
    '& > span': {
        marginRight: pxToRem(10),
    },
});
